import { ReportsGetRefreshDocument, ReportsRefreshDocument } from 'generated/resources/graphql'
import { isNil } from 'lodash'
import { useInterval } from 'usehooks-ts'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import app from 'modules/core/store/models/app'

type Params = {
  readOnly?: boolean
}

export const useUserETL = ({ readOnly = true }: Params) => {
  const { data: refreshData, refetch } = useQuery(ReportsGetRefreshDocument)
  const [launchUserETL, { loading: loadingLaunchUserETL }] = useMutation(ReportsRefreshDocument, {
    extractorFn: (data) => data.reportsMutations.createReportsRefresh,
  })

  const forceRefresh = useUnsafeCallback(async () => {
    const rawResult = await launchUserETL({
      variables: {
        force: true,
      },
    })

    handleResult(rawResult, (errorMessages) => {
      app.setFlash({
        type: 'wrong',
        message: Object.values(errorMessages).join(', '),
        time: 3000,
      })
    })
  }, [launchUserETL])

  const rawRefresh = refreshData?.reports?.refreshes?.[0]
  const rawLastRefreshTime = refreshData?.reports?.refreshes?.[0]?.lastRefreshTime
  const lastRefreshTime = rawLastRefreshTime
    ? stringToDate(rawLastRefreshTime, 'backend_datetime')
    : undefined

  const loading = isNil(rawRefresh)

  const isEmpty = rawRefresh?.isEmpty ?? true
  // If we just called launchUserETL, we show it as running until the request finishes
  const isRunning = loadingLaunchUserETL || (rawRefresh?.isRunning ?? false)

  useUnsafeEffect(() => {
    if (!readOnly && isEmpty && !isRunning && !loading && !loadingLaunchUserETL) {
      launchUserETL({
        variables: {
          force: false,
        },
      })
    }
  }, [readOnly, launchUserETL, isEmpty, isRunning, loading, loadingLaunchUserETL])

  useInterval(refetch, !readOnly && isRunning ? 10000 : null)

  return {
    loading,
    isEmpty,
    isRunning,
    lastRefreshTime,
    forceRefresh,
    refetch,
  }
}
